// Accounts — the business customers, and the party invoices are addressed to.
//
// Not an address book: built to answer "who owes us and who do I call". The
// billing fields (terms, ABN, addressee, logo) live here rather than on the
// person, because a job under a business invoices the business — "we invoice
// account, not the person" (Jacky, 2026-09-12).
//
// This is its own surface, NOT a tab on Contacts. Accounts are a separate
// table; the old People|Accounts tabs split by `contact_type` and could never
// match, so every business showed up as a person.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { Page } from "../shell/Page";
import { useAccounts } from "../hooks/useReads";
import { day } from "../lib/format";
import { BackToTop } from "../components/back-to-top";
import { ACCOUNT_CATEGORIES, termsLabel } from "../lib/accounts";
import { inputCls } from "../components/job-bits";

type Row = Record<string, unknown>;
const s = (v: unknown): string => (typeof v === "string" ? v : "");

export function Accounts() {
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string>("");
  useEffect(() => { const t = setTimeout(() => setQ(text.trim()), 250); return () => clearTimeout(t); }, [text]);

  const query: { q?: string; category?: string } = {};
  if (q) query.q = q;
  if (category) query.category = category;
  const accounts = useAccounts(query);
  const rows = (accounts.data ?? []) as Row[];

  return (
    <Page title="Accounts" lede="Business customers. A job under a business invoices the business, not the person.">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="relative block w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input className={`${inputCls} pl-8`} placeholder="business, ABN, or a person's name" value={text} onChange={(e) => setText(e.target.value)} aria-label="Search accounts" />
        </label>
        <select className={`${inputCls} max-w-[16rem]`} value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Filter by category">
          <option value="">All categories</option>
          {ACCOUNT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead className="text-left font-mono text-micro uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Business</th>
              <th className="px-3 py-2 font-medium">Category</th>
              <th className="hidden px-3 py-2 font-medium md:table-cell">ABN</th>
              <th className="px-3 py-2 font-medium">Terms</th>
              <th className="hidden px-3 py-2 text-right font-medium xl:table-cell">Updated</th>
            </tr>
          </thead>
          <tbody>
            {accounts.isPending && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">Loading accounts…</td></tr>}
            {accounts.isError && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">Accounts could not be loaded.</td></tr>}
            {!accounts.isPending && !accounts.isError && rows.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">{q || category ? "No business matches." : "No accounts yet."}</td></tr>
            )}
            {rows.map((a) => {
              const id = s(a.id);
              return (
                <tr key={id} className="border-t hover:bg-muted/50">
                  <td className="px-3 py-2">
                    <Link to={`/accounts/${id}`} className="font-medium hover:underline">{s(a.name) || "Unnamed"}</Link>
                    {s(a.short_name) && <span className="ml-2 text-micro text-muted-foreground">{s(a.short_name)}</span>}
                    {/* How many people to call at this business. Derived on read
                        from the contacts embed, so it cannot go stale. */}
                    {typeof a.people_count === "number" && (
                      <span className="ml-2 text-micro text-muted-foreground">
                        {a.people_count} {a.people_count === 1 ? "person" : "people"}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{s(a.category) || <span className="text-muted-foreground">—</span>}</td>
                  <td className="num hidden px-3 py-2 md:table-cell">{s(a.abn) || <span className="text-muted-foreground">not recorded</span>}</td>
                  <td className="px-3 py-2">{termsLabel(a.ar_days_expected)}</td>
                  <td className="num hidden px-3 py-2 text-right text-micro text-muted-foreground xl:table-cell">{a.updated_at ? day(String(a.updated_at)) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <BackToTop />
    </Page>
  );
}
